import { Component, Inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { BaseComponent, environment } from '@eview/core';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { inputTextValidator } from '../../shared/validators/inputText.validator';
import { inputText250Validator } from '../../shared/validators/inputText250.validator';
import { EContactUsActions, ContactUsPost } from '@eview/core/store/actions/contact-us.actions';
import { Subscription } from 'rxjs';
import { ToastService } from '../../toast/toast.service';
import { ToastType } from '../../toast/toast.component';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { executeRecaptcha } from '@eview/core/auth/recaptcha.helpers';

@Component({
  selector: 'eview-contact-us',
  templateUrl: 'contact-us.component.html',
  styleUrls: ['contact-us.component.scss']
})

export class ContactUsComponent extends BaseComponent implements OnInit, OnDestroy {

  contactUsForm: UntypedFormGroup;
  submitted = false;
  isVisible = false;
  isLoading = false;


  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private toast: ToastService,
    private fb: UntypedFormBuilder,
    private recaptchaV3Service: ReCaptchaV3Service,
    @Inject(ViewContainerRef) private viewContainerRef: ViewContainerRef
  ) {
    super();
    this.subs = new Subscription();
    toast.setViewContainerRef(viewContainerRef);
  }

  captchaCode: string;

  captchaConfig: any;

  ngOnInit() {
    this.initializeCaptcha();

    this.actions$
      .pipe(ofType(EContactUsActions.ContactUsSuccess))
      .subscribe(() => {
        this.resetForm();
        this.initForm();
        this.initializeCaptcha();
        this.toast.show(ToastType.Success, 'CONTACT_SUCCESS');
      });

    this.initForm();

  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private subs: Subscription;

  get control() { return this.contactUsForm.controls; }

  initForm() {
    this.contactUsForm = this.fb.group({
      name: new UntypedFormControl('', [Validators.required, inputTextValidator, Validators.maxLength(150)]),
      email: new UntypedFormControl('', [Validators.required, Validators.email, Validators.maxLength(150)]),
      alternative_email: new UntypedFormControl('', [Validators.email, Validators.maxLength(150)]),
      phone_number: new UntypedFormControl('', [Validators.required, Validators.maxLength(20), Validators.pattern(/^[0-9\s\+\-\(\)]+$/)]),
      subject: new UntypedFormControl('', [Validators.required, inputText250Validator]),
      message: new UntypedFormControl('', [Validators.required, inputText250Validator]),
      acceptedTerms: new UntypedFormControl(false, [this.mustBeTrueValidator()])
    });
  }

  initializeCaptcha() {
    this.captchaConfig = {
      type: 1,
      length: 6,
      cssClass: 'custom',
      back: {
        stroke: "#2F9688",
        solid: "#f2efd2"
      },
      font: {
        color: "#000000",
        size: "35px"
      }
    };
  }

  resetForm() {
    this.contactUsForm.reset();
    Object.keys(this.contactUsForm.controls).forEach(key => {
      this.contactUsForm.get(key).setErrors(null);
    });
    this.submitted = false
  }

  mustBeTrueValidator() {
    return (control: UntypedFormControl) => {
      return control.value === true ? null : { mustBeTrue: true };
    };
  }

  onCheckboxClick(event: Event): void {
    event.preventDefault();

    const control = this.contactUsForm.get('acceptedTerms');

    if (control.value) {
      setTimeout(() => {
        control.setValue(false);
      });
      return;
    }
    
    this.openModal();
  }

  openModal(): void {
    this.isVisible = true;
  }

  closeModal(): void {
    this.isVisible = false;
  }

  checkTermsConditions(): void {
    this.isLoading = true;
    const control = this.contactUsForm.get('acceptedTerms');
    control.setValue(true);
    this.closeModal();
  }

  onSubmit() {
    this.submitted = true;
    if (this.contactUsForm.valid) {
      executeRecaptcha(this.recaptchaV3Service).subscribe((token) => {
        this.store.dispatch(
          new ContactUsPost({
            ...this.contactUsForm.value,
            token: token
          })
        );
      })
    }
  }

  validarField(event: KeyboardEvent) {
		const char = event.key;
		const target = event.target as HTMLInputElement;
		const currentValue = target.value;
		const selectionStart = target.selectionStart || 0;
		const fieldName = target.getAttribute('formControlName');
		
		// Prevenir espacios al inicio
		if (selectionStart === 0 && char === ' ') {
			event.preventDefault();
			return;
		}
		
		// Prevenir escritura si excede límite de caracteres
		const maxLength = (fieldName === 'subject' || fieldName === 'message') ? 250 : 150;
		if (currentValue.length >= maxLength && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(char)) {
			event.preventDefault();
			return;
		}
		
		const regex = (fieldName === 'subject' || fieldName === 'message') 
			? /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s\-_@.+"()\[\],;°]$/
			: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s\-_@.+]$/
		if (!regex.test(char)) {
			event.preventDefault();
		}
	}

  onEmailKeypress(event: KeyboardEvent) {
    const char = String.fromCharCode(event.which);
    if (char === ' ') {
      event.preventDefault();
    }
  }

  onEmailInput(event: any, fieldName: string) {
    const emailPattern = /^[a-zA-Z0-9._@-]*$/;
    let value = event.target.value.replace(/\s/g, '');
    
    if (!emailPattern.test(value)) {
      value = value.replace(/[^a-zA-Z0-9._@-]/g, '');
      event.target.value = value;
      this.contactUsForm.controls[fieldName].setValue(value);
    }
  }

  onPaste(event: ClipboardEvent, fieldName: string) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    
    if (fieldName === 'name') {
      const filteredText = pastedText.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s\-]/g, '').trimStart();
      const currentValue = this.contactUsForm.get('name')?.value || '';
      const newValue = currentValue + filteredText;
      if (newValue.length <= 150) {
        this.contactUsForm.get('name')?.setValue(newValue);
      }
    } else if (fieldName === 'phone_number') {
      const filteredText = pastedText.replace(/[^0-9\s\+\-\(\)]/g, '').trimStart();
      const currentValue = this.contactUsForm.get('phone_number')?.value || '';
      const newValue = currentValue + filteredText;
      if (newValue.length <= 20) {
        this.contactUsForm.get('phone_number')?.setValue(newValue);
      }
    } else if (fieldName === 'email' || fieldName === 'alternative_email') {
      const filteredText = pastedText.replace(/[^a-zA-Z0-9._@-]/g, '').replace(/\s/g, '');
      const currentValue = this.contactUsForm.get(fieldName)?.value || '';
      const newValue = currentValue + filteredText;
      if (newValue.length <= 150) {
        this.contactUsForm.get(fieldName)?.setValue(newValue);
      }
    } else if (fieldName === 'subject' || fieldName === 'message') {
      const filteredText = pastedText.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s\-_@.+"()\[\],;°]/g, '').trimStart();
      const currentValue = this.contactUsForm.get(fieldName)?.value || '';
      const newValue = currentValue + filteredText;
      if (newValue.length <= 250) {
        this.contactUsForm.get(fieldName)?.setValue(newValue);
      }
    }
  }
  validarFieldNumber(event: KeyboardEvent) {
		const char = event.key;
		const target = event.target as HTMLInputElement;
		const currentValue = target.value;
		const selectionStart = target.selectionStart || 0;
		
		// Prevenir espacios al inicio
		if (selectionStart === 0 && char === ' ') {
			event.preventDefault();
			return;
		}
		
		// Prevenir escritura si excede 20 caracteres
		if (currentValue.length >= 20 && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(char)) {
			event.preventDefault();
			return;
		}
		
		const regex = /^[0-9\s\+\-\(\)]$/;
		if (!regex.test(char)) {
			event.preventDefault();
		}
	} 

}
